/**
 * Dashboard data layer — Mongo via /api/dashboard only.
 *
 * One-shot migration: if older browser-only boards still sit in localStorage
 * (ids starting with `local_`), the next successful list call POSTs them to
 * the server and clears the ones that migrated. New creates never touch
 * localStorage.
 */
import * as api from "../../../api/dashboard";

const KEY = "gmb_local_dashboards";

const readLocals = () => {
  try {
    return JSON.parse(localStorage.getItem(KEY) || "[]");
  } catch (e) {
    return [];
  }
};

const writeLocals = (arr) => {
  try {
    localStorage.setItem(KEY, JSON.stringify(arr));
  } catch (e) {
    /* storage full / unavailable — ignore */
  }
};

const isLocalId = (id) => typeof id === "string" && id.startsWith("local_");

/** Axios resolves the full response; keep a plain-object path for tests. */
const unwrap = (res) =>
  res && res.data !== undefined && res.status !== undefined ? res.data : res;

/** Push leftover browser-only boards once the API is reachable. */
const migrateLocals = async () => {
  const pending = readLocals().filter((d) => isLocalId(d._id));
  if (!pending.length) return [];

  const results = await Promise.all(
    pending.map(async (local) => {
      try {
        const rest = { ...local };
        delete rest._id;
        delete rest.createdAt;
        delete rest.updatedAt;
        const res = await api.CREATE_DASHBOARD(rest);
        const created = unwrap(res);
        if (created) return { ok: true, created, localId: local._id };
        return { ok: false };
      } catch (e) {
        return { ok: false };
      }
    }),
  );

  const migrated = results.filter((r) => r.ok).map((r) => r.created);
  const migratedIds = new Set(results.filter((r) => r.ok).map((r) => r.localId));
  writeLocals(readLocals().filter((d) => !migratedIds.has(d._id)));
  return migrated;
};

export const GET_DASHBOARDS = async () => {
  const res = await api.GET_DASHBOARDS();
  const serverRows = Array.isArray(unwrap(res)) ? unwrap(res) : [];
  const migrated = await migrateLocals();
  const byId = new Map();
  [...serverRows, ...migrated].forEach((d) => {
    if (d?._id) byId.set(String(d._id), d);
  });
  return {
    data: Array.from(byId.values()).sort(
      (a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0),
    ),
    local: false,
  };
};

export const GET_DASHBOARD = async (id) => {
  if (isLocalId(id)) {
    throw new Error("This dashboard is browser-only. Refresh to migrate it to the server.");
  }
  const res = await api.GET_DASHBOARD(id);
  return { data: unwrap(res), local: false };
};

export const CREATE_DASHBOARD = async (data) => {
  const res = await api.CREATE_DASHBOARD(data);
  return { data: unwrap(res), local: false };
};

export const UPDATE_DASHBOARD = async (id, data) => {
  if (isLocalId(id)) {
    throw new Error("This dashboard is browser-only. Refresh the Dashboards tab to save it to the database.");
  }
  const res = await api.UPDATE_DASHBOARD(id, data);
  return { data: unwrap(res), local: false };
};

export const SET_DEFAULT_DASHBOARD = async (id) => {
  if (isLocalId(id)) {
    throw new Error("This dashboard is browser-only. Refresh to migrate it first.");
  }
  const res = await api.SET_DEFAULT_DASHBOARD(id);
  return { data: unwrap(res), local: false };
};

export const DELETE_DASHBOARD = async (id) => {
  if (isLocalId(id)) {
    writeLocals(readLocals().filter((d) => d._id !== id));
    return { data: { ok: true, _id: id }, local: false };
  }
  const res = await api.DELETE_DASHBOARD(id);
  return { data: unwrap(res), local: false };
};
